import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import socketService from '../services/socketService';
import APIKit from '../../config/APIKit';

import io from 'socket.io-client';
import { BASE_URL, SOCKET_URL } from '../../config/config';
import { encryptMessage, decryptMessage } from '../utils/chatCrypto';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState({}); // { conversationId: [messages] }
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState({}); // { conversationId: [userId] }
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [socket, setSocket] = useState(null);

  // Support both old and new store structure
  const loggedUser = useSelector((state) => state.auth || state.logged || {});
  const [currentUserId, setCurrentUserId] = useState(null);

  // Fetch current user ID and setup socket connection
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        // Try to get from state first
        const userIdFromState = loggedUser?.user?.id || loggedUser?.user?.user?.id || loggedUser?.id || loggedUser?.userData?.id;
        const token = loggedUser?.token || loggedUser?.accessToken || localStorage.getItem('accessToken');

        let userId = userIdFromState;

        if (!userId && token) {
          // Fallback: fetch from API
          const res = await APIKit.get('/me');
          if (res && res.data) {
            userId = res.data.id || res.data.user?.id || res.id;
          }
        }

        if (userId && token) {
          setCurrentUserId(userId);

          // Connect to socket
          const socketUrl = SOCKET_URL || BASE_URL.replace('/api', '');
          const newSocket = io(socketUrl, {
            auth: {
              token: token,
              userId: userId
            },
            transports: ['websocket', 'polling']
          });

          setSocket(newSocket);
          socketService.socket = newSocket;
          socketService.isConnected = newSocket.connected || true;
          setupSocketListeners(newSocket, userId);
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };

    fetchCurrentUser();

    // Cleanup socket on unmount
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [loggedUser]);

  // Helper functions (defined first for use in setupSocketListeners)
  const updateConversationUnread = useCallback((conversationId, increment = true) => {
    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === conversationId
          ? { ...conv, unreadCount: (conv.unreadCount || 0) + (increment ? 1 : -1) }
          : conv
      )
    );
  }, []);

  const markMessageAsRead = useCallback(async (messageId) => {
    try {
      await APIKit.put(`/chat/messages/${messageId}/read`);
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  }, []);

  const showNotification = useCallback((message) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const conversation = conversations.find((c) => c.id === message.conversationId);
      const senderName = conversation?.otherUser?.firstName || 'Someone';
      
      new Notification(`${senderName} sent a message`, {
        body: decryptMessage(message.content, message.conversationId),
        icon: conversation?.otherUser?.avatar || '/default-avatar.jpg',
        tag: `chat-${message.conversationId}`
      });
    }
  }, [conversations]);

  // Setup socket event listeners
  const setupSocketListeners = useCallback((socketInstance, userId) => {
    if (!socketInstance) return;

    // Connection events
    socketInstance.on('connect', () => {
      console.log('Socket connected:', socketInstance.id);
      setSocketConnected(true);
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setSocketConnected(false);
      
      // Attempt to reconnect if not manually disconnected
      if (reason === 'io server disconnect') {
        // Server disconnected, reconnect manually
        socketInstance.connect();
      } else {
        // Client disconnected, will auto-reconnect
        console.log('Will attempt to reconnect...');
      }
    });

    socketInstance.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setSocketConnected(false);
    });

    socketInstance.on('reconnect', (attemptNumber) => {
      console.log('Socket reconnected after', attemptNumber, 'attempts');
      setSocketConnected(true);
    });

    socketInstance.on('reconnect_attempt', (attemptNumber) => {
      console.log('Reconnection attempt', attemptNumber);
    });

    socketInstance.on('reconnect_error', (error) => {
      console.error('Reconnection error:', error);
    });

    socketInstance.on('reconnect_failed', () => {
      console.error('Failed to reconnect to socket');
      setSocketConnected(false);
    });

    // Receive new message
    socketInstance.on('receive-message', (data) => {
      const { conversationId, message } = data;
      const decryptedMsg = message ? {
        ...message,
        content: decryptMessage(message.content, conversationId)
      } : message;

      setMessages((prev) => {
        const existingMessages = prev[conversationId] || [];
        // Check if message already exists (prevent duplicates)
        const messageExists = existingMessages.some(m => m.id === decryptedMsg.id);
        if (messageExists) {
          return prev;
        }
        
        // Add new message and sort by date
        const updatedMessages = [...existingMessages, decryptedMsg].sort((a, b) => {
          const dateA = new Date(a.createdAt || a.sentAt || 0);
          const dateB = new Date(b.createdAt || b.sentAt || 0);
          return dateA - dateB;
        });
        
        return {
          ...prev,
          [conversationId]: updatedMessages
        };
      });

      // Update conversation last message
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId
            ? { ...conv, lastMessage: decryptedMsg, updatedAt: decryptedMsg.createdAt }
            : conv
        )
      );

      // Update unread count if not active conversation
      if (activeConversation?.id !== conversationId) {
        setUnreadCount((prev) => prev + 1);
        updateConversationUnread(conversationId, true);
      } else {
        // Mark as read if active
        markMessageAsRead(decryptedMsg.id);
      }

      // Show notification
      showNotification(decryptedMsg);
    });

    // User online
    socketInstance.on('user-online', (data) => {
      setOnlineUsers((prev) => new Set([...prev, data.userId]));
    });

    // User offline
    socketInstance.on('user-offline', (data) => {
      setOnlineUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(data.userId);
        return newSet;
      });
    });

    // Typing indicator
    socketInstance.on('typing', (data) => {
      const { conversationId, userId: typingUserId } = data;
      if (typingUserId !== userId) {
        setTypingUsers((prev) => ({
          ...prev,
          [conversationId]: [...(prev[conversationId] || []), typingUserId]
        }));

        // Auto remove typing after 3 seconds
        setTimeout(() => {
          setTypingUsers((prev) => ({
            ...prev,
            [conversationId]: (prev[conversationId] || []).filter((id) => id !== typingUserId)
          }));
        }, 3000);
      }
    });

    // Stop typing
    socketInstance.on('stop-typing', (data) => {
      const { conversationId, userId: typingUserId } = data;
      setTypingUsers((prev) => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).filter((id) => id !== typingUserId)
      }));
    });

    // Message sent confirmation
    socketInstance.on('message-sent', (data) => {
      console.log('Message sent successfully:', data);
    });

    // Message error
    socketInstance.on('message-error', (data) => {
      console.error('Message send error:', data.error);
    });

  }, [activeConversation]);

  // Load conversations with guard to prevent multiple simultaneous calls
  const loadConversationsRef = useRef(false);
  const loadConversations = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (loadConversationsRef.current) {
      return;
    }
    
    try {
      loadConversationsRef.current = true;
      setLoading(true);
      const res = await APIKit.get('/chat/conversations');
      if (res.status) {
        const conversationsData = (res.data || []).map((conv) => {
          if (conv.lastMessage && conv.lastMessage.content) {
            return {
              ...conv,
              lastMessage: {
                ...conv.lastMessage,
                content: decryptMessage(conv.lastMessage.content, conv.id),
              },
            };
          }
          return conv;
        });
        setConversations(conversationsData);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
      loadConversationsRef.current = false;
    }
  }, []);

  // Load messages for a conversation with guard to prevent multiple simultaneous calls
  const loadMessagesRef = useRef({});
  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    
    // Prevent multiple simultaneous calls for the same conversation
    if (loadMessagesRef.current[conversationId]) {
      return;
    }
    
    try {
      loadMessagesRef.current[conversationId] = true;
      const res = await APIKit.get(`/chat/messages/${conversationId}`);
      
      if (res && (res.status || res.data)) {
        // Handle both response structures: { status: true, data: [...] } or direct array
        const messagesData = res.data?.data || res.data || [];
        
        if (Array.isArray(messagesData) && messagesData.length > 0) {
          // Ensure messages are decrypted and sorted by createdAt (oldest first)
          const decryptedMessages = messagesData.map((m) => ({
            ...m,
            content: decryptMessage(m.content, conversationId),
          }));
          const sortedMessages = [...decryptedMessages].sort((a, b) => {
            const dateA = new Date(a.createdAt || a.sentAt || 0);
            const dateB = new Date(b.createdAt || b.sentAt || 0);
            return dateA - dateB;
          });
          
          setMessages((prev) => ({
            ...prev,
            [conversationId]: sortedMessages
          }));
        } else {
          // Set empty array if no messages
          setMessages((prev) => ({
            ...prev,
            [conversationId]: []
          }));
        }

        // Join conversation room for real-time updates
        if (socket && socketConnected) {
          socket.emit('join-conversation', conversationId);
        }
      } else {
        console.warn('Failed to load messages - invalid response:', res);
        setMessages((prev) => ({
          ...prev,
          [conversationId]: []
        }));
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessages((prev) => ({
        ...prev,
        [conversationId]: []
      }));
    } finally {
      // Clear the guard after a short delay to allow re-fetching if needed
      setTimeout(() => {
        delete loadMessagesRef.current[conversationId];
      }, 2000);
    }
  }, [socket, socketConnected]);

  // Send message with End-to-End Encryption
  const sendMessage = useCallback(async (conversationId, plainContent, recipientId) => {
    try {
      // Encrypt message content on sender's device before transmission (E2EE)
      const encryptedContent = encryptMessage(plainContent, conversationId);

      const messageData = {
        conversationId,
        content: encryptedContent,
        recipientId,
        senderId: currentUserId
      };

      // Send via socket for real-time
      if (socket && socketConnected) {
        socket.emit('send-message', messageData);

        // Join conversation room if not already joined
        socket.emit('join-conversation', conversationId);
      }

      // Also save via API for persistence
      const res = await APIKit.post('/chat/messages', messageData);
      if (res.status && res.data) {
        const rawNewMessage = res.data;
        const newMessage = {
          ...rawNewMessage,
          content: decryptMessage(rawNewMessage.content, conversationId),
        };

        setMessages((prev) => {
          const existingMessages = prev[conversationId] || [];
          // Check if message already exists (prevent duplicates)
          const messageExists = existingMessages.some(m => m.id === newMessage.id);
          if (messageExists) {
            return prev;
          }
          
          // Add new message and sort by date
          const updatedMessages = [...existingMessages, newMessage].sort((a, b) => {
            const dateA = new Date(a.createdAt || a.sentAt || 0);
            const dateB = new Date(b.createdAt || b.sentAt || 0);
            return dateA - dateB;
          });
          
          return {
            ...prev,
            [conversationId]: updatedMessages
          };
        });

        // Update conversation
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId
              ? { ...conv, lastMessage: newMessage, updatedAt: newMessage.createdAt }
              : conv
          )
        );

        return newMessage;
      }
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }, [currentUserId, socket, socketConnected]);

  // Create or get conversation
  const getOrCreateConversation = useCallback(async (otherUserId) => {
    try {
      const res = await APIKit.post('/chat/conversations', {
        userId: otherUserId
      });
      
      if (res.status && res.data) {
        const conversation = res.data;
        
        // Add to conversations if not exists
        setConversations((prev) => {
          const exists = prev.find((c) => c.id === conversation.id);
          if (!exists) {
            return [...prev, conversation];
          }
          return prev;
        });

        return conversation;
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }, []);

  // Mark conversation as read
  const markConversationAsRead = useCallback(async (conversationId) => {
    try {
      await APIKit.put(`/chat/conversations/${conversationId}/read`);
      
      // Update local state using functional update to avoid dependency on conversations
      setConversations((prev) => {
        const conversation = prev.find((c) => c.id === conversationId);
        const unreadCount = conversation?.unreadCount || 0;
        
        // Update unread count
        if (unreadCount > 0) {
          setUnreadCount((prevCount) => Math.max(0, prevCount - unreadCount));
        }
        
        // Update conversation
        return prev.map((conv) =>
          conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
        );
      });
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  }, []);

  // Typing indicator
  const sendTyping = useCallback((conversationId, isTyping) => {
    if (!conversationId || !socket || !socketConnected) return;

    socket.emit(isTyping ? 'typing' : 'stop-typing', {
      conversationId,
      userId: currentUserId
    });
  }, [currentUserId, socket, socketConnected]);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }, []);

  // Load unread count
  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await APIKit.get('/chat/unread-count');
      if (res.status) {
        setUnreadCount(res.data?.count || 0);
      }
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    if (currentUserId) {
      loadConversations();
      loadUnreadCount();
      requestNotificationPermission();
    }
  }, [currentUserId, loadConversations, loadUnreadCount, requestNotificationPermission]);

  // Track which conversations have been loaded to prevent duplicate calls
  const loadedConversationsRef = useRef(new Set());
  const markReadRef = useRef({});
  const currentConversationIdRef = useRef(null);

  // Mark conversation as read when active
  useEffect(() => {
    if (!activeConversation?.id) {
      currentConversationIdRef.current = null;
      return;
    }
    
    const conversationId = activeConversation.id;
    
    // If it's the same conversation, don't reload (but ensure messages are loaded)
    if (currentConversationIdRef.current === conversationId) {
      // Check if messages exist, if not, load them
      if (!messages[conversationId] || messages[conversationId].length === 0) {
        console.log('Same conversation but no messages, loading...');
        loadMessages(conversationId);
      }
      return;
    }
    
    // Clear previous conversation guards
    const previousConversationId = currentConversationIdRef.current;
    if (previousConversationId) {
      delete markReadRef.current[previousConversationId];
      loadedConversationsRef.current.delete(previousConversationId);
      delete loadMessagesRef.current[previousConversationId];
    }
    
    // Update current conversation ID
    currentConversationIdRef.current = conversationId;
    
    // Prevent multiple simultaneous calls for the same conversation
    if (markReadRef.current[conversationId] || loadedConversationsRef.current.has(conversationId)) {
      return;
    }
    
    // Mark as loading
    markReadRef.current[conversationId] = true;
    loadedConversationsRef.current.add(conversationId);
    
    // Mark as read
    markConversationAsRead(conversationId).catch(err => {
      console.error('Error marking conversation as read:', err);
    }).finally(() => {
      setTimeout(() => {
        delete markReadRef.current[conversationId];
      }, 2000);
    });
    
    // Load messages - always load on conversation change
    loadMessages(conversationId).catch(err => {
      console.error('Error loading messages:', err);
    }).finally(() => {
      // Allow re-loading after 5 seconds if needed
      setTimeout(() => {
        loadedConversationsRef.current.delete(conversationId);
      }, 5000);
    });
    
    // Cleanup: clear refs when conversation changes
    return () => {
      // Don't clear if it's still the current conversation
      if (currentConversationIdRef.current !== conversationId) {
        delete markReadRef.current[conversationId];
        loadedConversationsRef.current.delete(conversationId);
        delete loadMessagesRef.current[conversationId];
      }
    };
    
    // Only depend on the conversation ID, not the entire object or callbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversation?.id]);

  const value = {
    conversations,
    activeConversation,
    setActiveConversation,
    messages: messages[activeConversation?.id] || [],
    onlineUsers,
    typingUsers: typingUsers[activeConversation?.id] || [],
    unreadCount,
    loading,
    socketConnected,
    socket,
    sendMessage,
    getOrCreateConversation,
    loadConversations,
    loadMessages,
    markMessageAsRead,
    markConversationAsRead,
    sendTyping,
    requestNotificationPermission,
    currentUserId
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

